/**
 * Applications API Client
 *
 * API functions for the self-service onboarding wizard (public endpoints)
 * and admin application management (authenticated endpoints).
 *
 * NOTE: axiosClient has a response interceptor that already extracts
 * response.data.data from ApiResponse format, so callers receive the
 * inner payload directly — no additional unwrapping needed.
 */

import type { Application, OnboardingFormData } from "../../types/onboarding";
import axiosClient from "./axiosClient";

// ── Public Endpoints (no auth, use application token) ──────────

/** Create a new draft application. Returns id + application_token. */
export async function createApplication(): Promise<{
	id: string;
	application_token: string;
}> {
	return axiosClient.post("/api/applications");
}

/** Load an existing draft by ID (requires application token). */
export async function getApplication(
	id: string,
	token: string,
): Promise<Application> {
	return axiosClient.get(`/api/applications/${id}`, {
		headers: { "X-Application-Token": token },
	});
}

/** Save draft (partial update, requires application token). */
export async function saveApplicationDraft(
	id: string,
	token: string,
	updates: Partial<OnboardingFormData> & { current_step?: number },
): Promise<void> {
	await axiosClient.patch(`/api/applications/${id}`, updates, {
		headers: { "X-Application-Token": token },
	});
}

/** Final submit (requires application token + CAPTCHA token). */
export async function submitApplication(
	id: string,
	token: string,
	captchaToken?: string,
): Promise<void> {
	await axiosClient.post(
		`/api/applications/${id}/submit`,
		{ captcha_token: captchaToken },
		{ headers: { "X-Application-Token": token } },
	);
}

/** Get a presigned S3 upload URL for a document. */
export async function getUploadUrl(
	id: string,
	token: string,
	fileName: string,
	fileType: string,
): Promise<{ upload_url: string; s3_key: string }> {
	return axiosClient.post(
		`/api/applications/${id}/upload`,
		{ file_name: fileName, content_type: fileType },
		{ headers: { "X-Application-Token": token } },
	);
}

// ── Admin Endpoints (authenticated) ────────────────────────────

export interface ApplicationListParams {
	status?: string;
	page?: number;
	limit?: number;
}

export interface PaginatedApplications {
	data: Application[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
}

/** List applications with pagination and optional status filter. */
export async function listApplications(
	params: ApplicationListParams = {},
): Promise<PaginatedApplications> {
	return axiosClient.get("/api/applications", { params });
}

/** Get application detail (admin — no token needed, uses auth). */
export async function getApplicationAdmin(id: string): Promise<Application> {
	return axiosClient.get(`/api/applications/${id}`);
}

/** Approve an application (creates wallet + user). */
export async function approveApplication(
	id: string,
): Promise<{ wallet_id: string; user_id: string }> {
	return axiosClient.post(`/api/applications/${id}/approve`);
}

/** Reject an application with reviewer notes. */
export async function rejectApplication(
	id: string,
	reviewerNotes: string,
): Promise<void> {
	await axiosClient.post(`/api/applications/${id}/reject`, {
		reviewer_notes: reviewerNotes,
	});
}
